import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
    Alert, AlertProps, Button, Space, theme, Typography,
} from 'antd';
import { ReactNode } from 'react';

type ScriptChangesNotificationProps = {
    onOpenDetails: () => void;
    onAcknowledge: () => void;
    acknowledgeLoading?: boolean;
    lastChangedAt?: string | null;
    title?: string;
    datePrefix?: string;
    detailsLabel?: string;
    acknowledgeLabel?: string;
    description?: ReactNode;
    type?: AlertProps['type'];
    showDetailsButton?: boolean;
};

const formatChangedAt = (changedAt?: string | null): string | null => {
    if (!changedAt) {
        return null;
    }

    const parsedDate = new Date(changedAt);

    if (Number.isNaN(parsedDate.getTime())) {
        return changedAt;
    }

    return new Intl.DateTimeFormat('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(parsedDate);
};

export const ScriptChangesNotification = ({
    onOpenDetails,
    onAcknowledge,
    acknowledgeLoading = false,
    lastChangedAt,
    title = 'Скрипт оновлено',
    datePrefix = 'Остання зміна',
    detailsLabel = 'Деталі',
    acknowledgeLabel = 'Прочитано',
    description,
    type = 'error',
    showDetailsButton = true,
}: ScriptChangesNotificationProps) => {
    const { token } = theme.useToken();
    const formattedChangedAt = formatChangedAt(lastChangedAt);
    const isWarning = type === 'warning';
    const backgroundColor = isWarning ? token.colorWarningBg : token.colorErrorBg;
    const borderColor = isWarning ? token.colorWarning : token.colorError;
    const textColor = isWarning ? token.colorWarning : token.colorError;

    return (
        <Alert
            message={(
                <Space size={8} wrap>
                    <Typography.Text strong>{title}</Typography.Text>
                    {formattedChangedAt && (
                        <Typography.Text type="secondary">
                            {datePrefix}: {formattedChangedAt}
                        </Typography.Text>
                    )}
                </Space>
            )}
            description={typeof description === 'string'
                ? <span style={{ whiteSpace: 'pre-line' }}>{description}</span>
                : description}
            type={type}
            icon={<ExclamationCircleOutlined />}
            showIcon
            action={(
                <Space size={4}>
                    {showDetailsButton && (
                        <Button size="small" onClick={onOpenDetails}>
                            {detailsLabel}
                        </Button>
                    )}
                    <Button size="small" type="primary" onClick={onAcknowledge} loading={acknowledgeLoading}>
                        {acknowledgeLabel}
                    </Button>
                </Space>
            )}
            style={{
                backgroundColor,
                borderColor,
                color: textColor,
                marginBottom: token.marginSM,
                padding: `${token.paddingSM}px`,
            }}
        />
    );
};
